import {useQuery} from "@tanstack/react-query";
import {createFileRoute, useNavigate, useParams} from '@tanstack/react-router'
import React, {useMemo} from "react";
import {apiRetrieve} from "../../../../api.ts";
import Content from "../../../../components/Content.tsx";
import Header from "../../../../Header.tsx";
import {useJwtUser} from "../../../../JWTAuthProvider.tsx";
import '../../../../styles/player-dashboard.scss';
import RecordTable, {RecordRow} from "../../../../user/player-dashboard/RecordTable.tsx";
import queryKeys from "../../../../utilities/queryKeys.ts";

export const Route = createFileRoute('/_app/profiles/$player_uid/stats')({
    component:      RouteComponent,
    staticData:     {
        backUrl: "/profiles/$player_uid/dashboard/"
    },
    validateSearch: (s) => {
        return {subpage: s.subpage as string}
    }
})

// The table sorts on the client; the request keeps the sort it has always sent.
const SORT = {pair: {column: 'wins_percent', direction: 'ascending'}, pvp: null};

function RouteComponent() {
    const search = Route.useSearch();
    const params = useParams({strict: false});
    const navigate = useNavigate();
    const user = useJwtUser();
    const isPair = search.subpage === 'pair';

    const rankingQuery = useQuery({
        queryKey: queryKeys.player_stats(SORT),
        queryFn:  async () => apiRetrieve('player-stats' as any, {sort: SORT}),
        enabled:  !!user?.uid,
    })

    const playersMap = rankingQuery.data?.players;
    const pairStats = rankingQuery.data?.pair;
    const mine = user?.uid ? rankingQuery.data?.single?.[user.uid] : null;

    // Singles results arrive as a "pair" with no second player — not a partnership, so left out.
    const pairRows = useMemo<RecordRow[]>(() => (pairStats || []).filter((p: any) => p.p1 && p.p2).map((p: any) => ({
        key:    p.pair_ref,
        name:   `${playersMap?.[p.p1]?.name ?? '?'} & ${playersMap?.[p.p2]?.name ?? '?'}`,
        played: p.played || 0,
        won:    p.wins || 0,
    })), [pairStats, playersMap]);

    // Only players actually faced: `players` also lists partners, who have no head-to-head record.
    const pvpRows = useMemo<RecordRow[]>(() => Object.keys(mine || {}).map((uid) => ({
        key:    uid,
        name:   playersMap?.[uid]?.name ?? '?',
        played: mine[uid]?.played || 0,
        won:    mine[uid]?.won || 0,
    })).filter((r) => r.played > 0), [mine, playersMap]);

    const go = (subpage: string) => navigate({to: "/profiles/$player_uid/stats/", params: {player_uid: params.player_uid as string}, search: {subpage}});

    return <>
        <Header />

        <Content className="max-w-[1500px] w-full px-4 pb-6">
            <div className="player-dash">
                <div className="pd-page-head">
                    <div>
                        <h1 className="pd-page-title">{isPair ? 'Partnerships' : 'Player vs Player'}</h1>
                        <p className="pd-page-sub">{isPair ? 'How you do with each partner you have played alongside.' : 'Your head-to-head record against everyone you have faced.'}</p>
                    </div>
                    <div className="pd-segmented" role="group" aria-label="Statistics view">
                        <button type="button" aria-pressed={isPair} onClick={() => go('pair')}>Pair</button>
                        <button type="button" aria-pressed={!isPair} onClick={() => go('pvp')}>PvP</button>
                        <button type="button" aria-pressed={false} onClick={() => navigate({to: "/profiles/$player_uid/dashboard/", params: {player_uid: params.player_uid as string}})}>Player</button>
                    </div>
                </div>

                {/* The read failed (the API answers an error object, not a rejection): say so, rather
                    than the empty state telling someone with dozens of matches to go and play one. */}
                {(rankingQuery.data as any)?.error && (
                    <p className="mb-3 rounded-lg bg-danger-50 px-3 py-2 text-sm text-danger">
                        Could not load your records: {String((rankingQuery.data as any).message || (rankingQuery.data as any).error)}
                    </p>
                )}
                {(rankingQuery.data as any)?.history_days && (
                    <p className="mb-3 rounded-lg bg-warning-50 px-3 py-2 text-sm text-warning-700">
                        Showing your last {(rankingQuery.data as any).history_days} days. Player Pro keeps your whole history.
                    </p>
                )}
                {isPair
                    ? <RecordTable key="pair" rows={pairRows} nameHeader="Partnership" loading={rankingQuery.isLoading}
                                   labels={{count: 'Partnerships', best: 'Best Partnership', worst: 'Lowest Win Rate', empty: 'Play a doubles match and your partnerships show up here.'}} />
                    : <RecordTable key="pvp" rows={pvpRows} nameHeader="Opponent" loading={rankingQuery.isLoading}
                                   labels={{count: 'Opponents Faced', best: 'Best Record', worst: 'Toughest Opponent', empty: 'Play a match and your head-to-head records show up here.'}} />
                }
            </div>
        </Content>
    </>
}
